import { RadarCustomLayer } from '@nivo/radar';
import { RadarLayerId } from '@nivo/radar';
import { RadarSliceTooltipProps } from '@nivo/radar';
import React, { createContext, useMemo } from 'react';
import { predictionsChart } from 'apps/sigma2-ui/lib/design-tokens/nivo-themes';
import { primary } from 'apps/sigma2-ui/lib/design-tokens/nivo-themes';
import { RadarChart } from 'apps/sigma2-ui/src/components/RadarChart';
import cn from 'classnames';
import { RadarCustomLayerProps } from '@nivo/radar';
import { fillArray } from 'libs/end-to-end/src/factories/fillArray';
import { DefaultCalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { adjustCalibrationScoreForGraph } from 'apps/sigma2-ui/src/lib/graph-helpers';

const GRID_LEVELS = 6;

export interface DefaultPolarDataPoint extends Record<string, unknown> {
  label: string;
  sliceLink?: string;
  calibrationScore?: number;
  passingTests?: number;
  incompleteTests?: number;
  totalTests?: number;
}

export interface PredictionsPolarDataPoint extends Record<string, unknown> {
  label: string;
  sliceLink?: string;
  calibrationScore?: number;
  predictedCalibrationScore?: number;
  passingTests?: number;
  incompleteTests?: number;
  failingTests?: number;
}

interface DefaultPolarChartProps {
  variant: 'default';
  dataPoints: DefaultPolarDataPoint[];
  tooltip?: React.FC<RadarSliceTooltipProps>;
  onDotClick?: () => void;
  customLayers?: (RadarLayerId | RadarCustomLayer<DefaultPolarDataPoint>)[];
  calibrationFraming: CalibrationFraming;
  dataTestId?: string;
}

interface PredictionsPolarChartProps {
  variant: 'predictions';
  dataPoints: PredictionsPolarDataPoint[];
  tooltip?: React.FC<RadarSliceTooltipProps>;
  onDotClick?: () => void;
  customLayers?: (RadarLayerId | RadarCustomLayer<PredictionsPolarDataPoint>)[];
  calibrationFraming: CalibrationFraming;
  dataTestId?: string;
}

type PolarChartProps = DefaultPolarChartProps | PredictionsPolarChartProps;

interface DefaultPolarChartContext {
  dataPoints: DefaultPolarDataPoint[];
  calibrationFraming: CalibrationFraming;
}

interface PredictionsPolarChartContext {
  dataPoints: PredictionsPolarDataPoint[];
  calibrationFraming: CalibrationFraming;
}

export const DefaultPolarChartContext = createContext<DefaultPolarChartContext>(
  {
    dataPoints: [],
    calibrationFraming: DefaultCalibrationFraming,
  },
);

export const PredictionsPolarChartContext =
  createContext<PredictionsPolarChartContext>({
    dataPoints: [],
    calibrationFraming: DefaultCalibrationFraming,
  });

const DimensionDotSymbol = ({
  fill,
  onClick,
}: {
  fill: string;
  onClick?: () => void;
}) => {
  return (
    <circle
      cx={0}
      cy={0}
      r={8}
      z={-1}
      strokeWidth="2"
      stroke="#400977"
      fill={fill}
      onClick={onClick}
      className={cn({
        'hover:cursor-pointer': !!onClick,
      })}
      pointerEvents={'visible'}
    />
  );
};

const getDataPointColorByNumber = (
  value: number,
  calibrationFraming: CalibrationFraming,
) => {
  const numberToHexMapping: Record<number, string> = {
    6: calibrationFraming === 'bias' ? '#EE3F3F' : '#809EF4',
    5: calibrationFraming === 'bias' ? '#EE3F3F' : '#9696FA',
    4: calibrationFraming === 'bias' ? '#F671B3' : '#A890F1',
    3: calibrationFraming === 'bias' ? '#BF89F5' : '#BF89F5',
    2: calibrationFraming === 'bias' ? '#A890F1' : '#F671B3',
    1: calibrationFraming === 'bias' ? '#9696FA' : '#EE3F3F',
    0: calibrationFraming === 'bias' ? '#809EF4' : '#EE3F3F',
  };

  const calibrationScoreRounded = Math.floor(value);
  return numberToHexMapping[calibrationScoreRounded];
};

/**
 * If is even grid line, draw a dashed line, if odd draw solid line
 */
const DottedGridLayer: React.FC<
  RadarCustomLayerProps<Record<string, never>>
> = ({ centerX, centerY, radiusScale }) => {
  return (
    <>
      {fillArray({
        length: GRID_LEVELS,
        valueFn(index) {
          return index + 1;
        },
      }).map((lineNum, i) => {
        const radius = radiusScale(lineNum);
        if (i % 2 === 0) {
          return (
            <circle
              key={`dotted-grid-${lineNum}`}
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="none"
              stroke="#D6D8D9"
              strokeWidth={1}
              strokeDasharray="4"
            />
          );
        }

        return (
          <circle
            key={`dotted-grid-${lineNum}`}
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="none"
            stroke="#D6D8D9"
            strokeWidth={1}
          />
        );
      })}
    </>
  );
};

const DefaultPolarChart = ({
  dataPoints,
  tooltip,
  onDotClick,
  customLayers,
  calibrationFraming,
  dataTestId,
}: {
  dataPoints: DefaultPolarDataPoint[];
  tooltip: React.FC<RadarSliceTooltipProps> | undefined;
  onDotClick?: () => void;
  customLayers?: (RadarLayerId | RadarCustomLayer<DefaultPolarDataPoint>)[];
  calibrationFraming: CalibrationFraming;
  dataTestId?: string;
}) => {
  const chartTooltip = tooltip ?? (() => <></>);
  // DO NOT CHANGE THE ORDER OF THESE LAYERS, changing the order will break the chart gradient which
  // uses the order of the layers to apply the gradient.
  const chartLayers = [
    'grid' as RadarLayerId,
    'layers' as RadarLayerId,
    'slices' as RadarLayerId,
    'dots' as RadarLayerId,
    'legends' as RadarLayerId,
    DottedGridLayer,
    ...(customLayers ?? []),
  ];

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxHeight: '100%',
        aspectRatio: '1/1',
      }}
      className=""
    >
      <RadarChart
        theme={{
          ...primary,
          ...{
            grid: {
              line: {
                stroke: '#D6D8D9',
                strokeWidth: 1,
                strokeDasharray: '4',
              },
            },
          },
        }}
        dataTestId={dataTestId}
        borderColor={'#400977'}
        curve={'catmullRomClosed'}
        data={dataPoints.map((dataPoint) => ({
          ...dataPoint,
          adjustedScore: adjustCalibrationScoreForGraph(
            dataPoint.calibrationScore ?? 0,
            calibrationFraming,
          ),
        }))}
        maxValue={6}
        gridLevels={GRID_LEVELS}
        gridShape={'circular'}
        indexBy={'label'}
        valueFormat={'>-.2f'}
        keys={['adjustedScore']}
        layers={chartLayers}
        motionConfig={'default'}
        dotColor={(dataPoint) =>
          getDataPointColorByNumber(dataPoint.value, calibrationFraming)
        }
        dotSymbol={(dataPoint) => {
          return (
            <DimensionDotSymbol fill={dataPoint.color} onClick={onDotClick} />
          );
        }}
        sliceTooltip={chartTooltip}
        gradientFills={[
          calibrationFraming === 'bias'
            ? 'radialGradientFigmaPurpleBias'
            : 'radialGradientFigmaPurpleOptimization',
        ]}
        levelLabelsConfig={{
          showLevelLabels: true,
        }}
        animate={false}
      />
    </div>
  );
};

const PredictionsPolarChart = ({
  dataPoints,
  tooltip,
  onDotClick,
  customLayers,
  calibrationFraming,
  dataTestId,
}: {
  dataPoints: PredictionsPolarDataPoint[];
  tooltip: React.FC<RadarSliceTooltipProps> | undefined;
  onDotClick?: () => void;
  customLayers?: (RadarLayerId | RadarCustomLayer<PredictionsPolarDataPoint>)[];
  calibrationFraming: CalibrationFraming;
  dataTestId?: string;
}) => {
  const chartTooltip = tooltip ?? (() => <></>);
  // DO NOT CHANGE THE ORDER OF THESE , changing gradient which
  // uses the order of the layers to apply the gradient.
  const chartLayers = [
    'grid' as RadarLayerId,
    'layers' as RadarLayerId,
    'slices' as RadarLayerId,
    'dots' as RadarLayerId,
    'legends' as RadarLayerId,
    DottedGridLayer,
    ...(customLayers ?? []),
  ];

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxHeight: '100%',
        aspectRatio: '1/1',
      }}
      className="bg-blue-1000"
    >
      <RadarChart
        theme={predictionsChart}
        borderColor={'#ffffff'}
        borderWidth={1}
        curve={'catmullRomClosed'}
        dataTestId={dataTestId}
        data={dataPoints.map((dataPoint) => ({
          ...dataPoint,
          adjustedScore: adjustCalibrationScoreForGraph(
            dataPoint.calibrationScore ?? 0,
            calibrationFraming,
          ),
          adjustedPredictedScore: adjustCalibrationScoreForGraph(
            dataPoint.predictedCalibrationScore ?? 0,
            calibrationFraming,
          ),
        }))}
        maxValue={6}
        gridLevels={6}
        gridShape={'circular'}
        indexBy={'label'}
        valueFormat={'>-.2f'}
        keys={['adjustedPredictedScore', 'adjustedScore']}
        layers={chartLayers}
        motionConfig={'default'}
        dotColor={(dataPoint) =>
          getDataPointColorByNumber(dataPoint.value, calibrationFraming)
        }
        dotSymbol={(dataPoint) => {
          return (
            <DimensionDotSymbol fill={dataPoint.color} onClick={onDotClick} />
          );
        }}
        sliceTooltip={chartTooltip}
        gradientFills={[
          calibrationFraming === 'bias'
            ? 'radialGradientFigmaPurpleBias'
            : 'radialGradientFigmaPurpleOptimization',
        ]}
        levelLabelsConfig={{
          showLevelLabels: true,
          labelFill: '#ffffff',
        }}
        animate={false}
      />
    </div>
  );
};

export const PolarChart = ({
  variant,
  dataPoints,
  tooltip,
  onDotClick,
  customLayers,
  calibrationFraming,
  dataTestId,
}: PolarChartProps) => {
  const chart = useMemo(() => {
    if (variant === 'predictions') {
      return (
        <PredictionsPolarChartContext.Provider
          value={{ dataPoints, calibrationFraming }}
        >
          <PredictionsPolarChart
            dataPoints={dataPoints}
            tooltip={tooltip}
            onDotClick={onDotClick}
            customLayers={customLayers}
            calibrationFraming={calibrationFraming}
            dataTestId={dataTestId}
          />
        </PredictionsPolarChartContext.Provider>
      );
    }

    return (
      <DefaultPolarChartContext.Provider
        value={{ dataPoints, calibrationFraming }}
      >
        <DefaultPolarChart
          dataPoints={dataPoints}
          tooltip={tooltip}
          onDotClick={onDotClick}
          customLayers={customLayers}
          calibrationFraming={calibrationFraming}
          dataTestId={dataTestId}
        />
      </DefaultPolarChartContext.Provider>
    );
  }, [
    variant,
    dataPoints,
    tooltip,
    onDotClick,
    customLayers,
    calibrationFraming,
    dataTestId,
  ]);

  return chart;
};
